import { useState, useMemo, useCallback } from "react";
import LiveSearchPanel from "@/components/LiveSearchPanel";
import IntranetLayout from "@/components/IntranetLayout";
import {
  FLEET, BD_OPPORTUNITIES, MARKET_SUMMARY, BD_CONTEXT, UPDATE_PROTOCOL,
  CATEGORY_CONFIG, ALL_REGIONS,
  type Vessel, type VesselCategory, type BDOpportunity
} from "@/data/fleetIntelligenceData";
import velocityLogoFull from "@/assets/velocity-logo-full-color.jpg";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// ─── STYLE HELPERS (light-theme semantic) ───
const TYPE_COLORS: Record<string, { pill: string; dot: string }> = {
  FPSO:      { pill: "bg-emerald-50 border-emerald-200 text-emerald-700",   dot: "bg-emerald-500" },
  FSO:       { pill: "bg-teal-50 border-teal-200 text-teal-700",            dot: "bg-teal-500" },
  FPS:       { pill: "bg-violet-50 border-violet-200 text-violet-700",      dot: "bg-violet-500" },
  FPU:       { pill: "bg-violet-50 border-violet-200 text-violet-700",      dot: "bg-violet-500" },
  TLP:       { pill: "bg-violet-50 border-violet-200 text-violet-700",      dot: "bg-violet-500" },
  SPAR:      { pill: "bg-violet-50 border-violet-200 text-violet-700",      dot: "bg-violet-500" },
  Drillship: { pill: "bg-rose-50 border-rose-200 text-rose-700",            dot: "bg-rose-500" },
  Jackup:    { pill: "bg-pink-50 border-pink-200 text-pink-700",            dot: "bg-pink-500" },
  Semi:      { pill: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700",   dot: "bg-fuchsia-500" },
  PSV:       { pill: "bg-blue-50 border-blue-200 text-blue-700",            dot: "bg-blue-500" },
  AHTS:      { pill: "bg-sky-50 border-sky-200 text-sky-700",               dot: "bg-sky-500" },
  MPSV:      { pill: "bg-cyan-50 border-cyan-200 text-cyan-700",            dot: "bg-cyan-500" },
  FSV:       { pill: "bg-amber-50 border-amber-200 text-amber-700",         dot: "bg-amber-500" },
  FSIV:      { pill: "bg-orange-50 border-orange-200 text-orange-700",      dot: "bg-orange-500" },
  CTV:       { pill: "bg-lime-50 border-lime-200 text-lime-700",            dot: "bg-lime-500" },
  "CTV/FSIV":{ pill: "bg-lime-50 border-lime-200 text-lime-700",            dot: "bg-lime-500" },
  SOV:       { pill: "bg-indigo-50 border-indigo-200 text-indigo-700",       dot: "bg-indigo-500" },
  CSV:       { pill: "bg-slate-100 border-slate-200 text-slate-600",         dot: "bg-slate-400" },
  HLCV:      { pill: "bg-slate-100 border-slate-200 text-slate-600",         dot: "bg-slate-400" },
  Pipelay:   { pill: "bg-slate-100 border-slate-200 text-slate-600",         dot: "bg-slate-400" },
  WTIV:      { pill: "bg-slate-100 border-slate-200 text-slate-600",         dot: "bg-slate-400" },
  Floatel:   { pill: "bg-slate-100 border-slate-200 text-slate-600",         dot: "bg-slate-400" },
  Survey:    { pill: "bg-slate-100 border-slate-200 text-slate-600",         dot: "bg-slate-400" },
  Seismic:   { pill: "bg-slate-100 border-slate-200 text-slate-600",         dot: "bg-slate-400" },
};

const REGION_COLORS: Record<string, string> = {
  "Guyana":       "bg-emerald-50 border-emerald-200 text-emerald-700",
  "Brazil":       "bg-blue-50 border-blue-200 text-blue-700",
  "West Africa":  "bg-yellow-50 border-yellow-200 text-yellow-700",
  "North Sea":    "bg-slate-100 border-slate-200 text-slate-600",
  "Asia-Pacific": "bg-purple-50 border-purple-200 text-purple-700",
  "South America":"bg-cyan-50 border-cyan-200 text-cyan-700",
  "North America":"bg-rose-50 border-rose-200 text-rose-700",
  "Middle East":  "bg-orange-50 border-orange-200 text-orange-700",
};

const REL_COLORS: Record<string, string> = {
  HIGH:   "bg-red-50 border-red-200 text-red-700",
  MEDIUM: "bg-yellow-50 border-yellow-200 text-yellow-700",
  LOW:    "bg-slate-100 border-slate-200 text-slate-500",
};

const STATUS_COLORS: Record<string, string> = {
  "Active":             "text-emerald-600",
  "Under Construction": "text-yellow-600",
  "Planned":            "text-blue-600",
};

const DELIVERY_COLORS = (d: string | null) => {
  if (!d) return "";
  const y = parseInt(d);
  if (y <= 2026) return "text-emerald-600 font-bold";
  if (y === 2027) return "text-yellow-600 font-semibold";
  return "text-orange-600";
};

const PRIORITY_COLORS: Record<string, string> = {
  "TOP PRIORITY": "bg-red-50 border-red-200 text-red-700",
  "HIGH":         "bg-orange-50 border-orange-200 text-orange-700",
  "MEDIUM":       "bg-yellow-50 border-yellow-200 text-yellow-700",
  "PIPELINE":     "bg-blue-50 border-blue-200 text-blue-700",
  "AVOID NEAR TERM": "bg-slate-100 border-slate-200 text-slate-500",
};

function TypeBadge({ type }: { type: string }) {
  const c = TYPE_COLORS[type] || TYPE_COLORS.PSV;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold ${c.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
      {type}
    </span>
  );
}

function RegionBadge({ region }: { region: string }) {
  const c = REGION_COLORS[region] || REGION_COLORS["North Sea"];
  return <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${c}`}>{region}</span>;
}

function RelBadge({ rel }: { rel: string }) {
  return <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-bold ${REL_COLORS[rel] || REL_COLORS.LOW}`}>{rel}</span>;
}

type MainTab = VesselCategory | "bd" | "summary" | "intel";

const TAB_ORDER: { key: MainTab; label: string; icon: string }[] = [
  { key: "summary",     label: "Market Summary",  icon: "📊" },
  { key: "production",  label: "Production",       icon: "🛢️" },
  { key: "drilling",    label: "Drilling",          icon: "⛏️" },
  { key: "osv",         label: "Supply & Support",  icon: "🚢" },
  { key: "ctv",         label: "Crew Transfer",     icon: "⚡" },
  { key: "specialised", label: "Specialised",       icon: "🔧" },
  { key: "bd",          label: "BD Intelligence",   icon: "🎯" },
  { key: "intel",       label: "Market Intel",      icon: "🤖" },
];

// ─── INTEL CATEGORY CONFIG ───
const INTEL_CAT_CONFIG: Record<string, { label: string; icon: string; color: string; borderColor: string }> = {
  tender:         { label: "Tenders & RFQ",    icon: "📋", color: "bg-amber-50 text-amber-700",   borderColor: "border-amber-200" },
  news:           { label: "Industry News",    icon: "📰", color: "bg-blue-50 text-blue-700",     borderColor: "border-blue-200" },
  award:          { label: "Awards & Contracts",icon: "🏆", color: "bg-emerald-50 text-emerald-700", borderColor: "border-emerald-200" },
  market_insight: { label: "Market Insights",  icon: "📈", color: "bg-purple-50 text-purple-700", borderColor: "border-purple-200" },
};

type IntelItem = {
  id: string; category: string; title: string; summary: string; source: string | null;
  source_url: string | null; region: string | null; operator: string | null;
  vessel_type: string | null; vessel_recommendation: string | null; velocity_fit: string | null;
  priority: string | null; tender_type: string | null; estimated_value: string | null;
  deadline: string | null; ai_analysis: string | null; is_actionable: boolean;
  is_archived: boolean; fetched_at: string; created_at: string;
};

function MarketIntelPanel() {
  const queryClient = useQueryClient();
  const [intelFilter, setIntelFilter] = useState<string>("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["fleet-intelligence-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_intelligence_items")
        .select("*")
        .eq("is_archived", false)
        .order("fetched_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as IntelItem[];
    },
  });

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    toast.info("Generating AI intelligence… this may take 30–60 seconds");
    try {
      const { data, error } = await supabase.functions.invoke("fleet-intelligence", {
        body: {},
      });
      if (error) throw error;
      toast.success(`Intelligence updated: ${data.items_generated} items generated`);
      queryClient.invalidateQueries({ queryKey: ["fleet-intelligence-items"] });
    } catch (err: any) {
      console.error("Intelligence refresh error:", err);
      toast.error("Failed to refresh intelligence: " + (err.message || "Unknown error"));
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  const filtered = useMemo(() => {
    if (intelFilter === "all") return items;
    return items.filter(i => i.category === intelFilter);
  }, [items, intelFilter]);

  const categoryCounts = useMemo(() => {
    const c: Record<string, number> = { all: items.length };
    items.forEach(i => { c[i.category] = (c[i.category] || 0) + 1; });
    return c;
  }, [items]);

  const actionableCount = items.filter(i => i.is_actionable).length;
  const topPriorityCount = items.filter(i => i.priority === "TOP PRIORITY" || i.priority === "HIGH").length;
  const lastFetched = items.length > 0 ? new Date(items[0].fetched_at).toLocaleString() : "Never";

  return (
    <div className="space-y-6">
      {/* Hero banner */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] text-blue-600 uppercase mb-1">🤖 AI-Powered Market Intelligence</div>
            <h3 className="text-lg font-black text-foreground font-display">Automated Fleet Intelligence</h3>
            <p className="text-muted-foreground text-sm mt-1">AI-curated tenders, news, awards, and market insights · Auto-refreshes daily</p>
            <p className="text-[10px] text-muted-foreground mt-1">Last updated: {lastFetched}</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl text-xs font-bold tracking-wide transition-all shadow-sm"
          >
            {isRefreshing ? (
              <><span className="animate-spin">⟳</span> Generating…</>
            ) : (
              <>🔄 Refresh Intelligence</>
            )}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mt-4">
          {[
            { v: items.length, l: "Total Items", c: "text-foreground" },
            { v: actionableCount, l: "Actionable", c: "text-emerald-600" },
            { v: topPriorityCount, l: "High Priority", c: "text-red-600" },
            { v: categoryCounts.tender || 0, l: "Open Tenders", c: "text-amber-600" },
          ].map(s => (
            <div key={s.l} className="bg-white/70 border border-white rounded-xl px-3 py-2.5 text-center">
              <div className={`text-2xl font-black ${s.c} tabular-nums`}>{s.v}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setIntelFilter("all")}
          className={`px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${
            intelFilter === "all" ? "bg-foreground text-background border-foreground" : "bg-muted/30 border-border text-muted-foreground hover:text-foreground"
          }`}>All <span className="opacity-60">{categoryCounts.all || 0}</span></button>
        {Object.entries(INTEL_CAT_CONFIG).map(([key, cfg]) => (
          <button key={key} onClick={() => setIntelFilter(key)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${
              intelFilter === key ? `${cfg.color} ${cfg.borderColor}` : "bg-muted/30 border-border text-muted-foreground hover:text-foreground"
            }`}>
            {cfg.icon} {cfg.label} <span className="opacity-60">{categoryCounts[key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-16 text-muted-foreground">Loading intelligence data…</div>
      )}

      {/* Empty state */}
      {!isLoading && items.length === 0 && (
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
          <div className="text-5xl mb-4">🤖</div>
          <h3 className="text-lg font-bold text-foreground mb-2">No Intelligence Data Yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Click "Refresh Intelligence" to generate your first AI-powered market report.</p>
          <button onClick={handleRefresh} disabled={isRefreshing}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl text-sm font-bold transition-all">
            {isRefreshing ? "Generating…" : "Generate Intelligence Now"}
          </button>
        </div>
      )}

      {/* Intelligence cards */}
      {filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(item => (
            <IntelCard key={item.id} item={item} />
          ))}
        </div>
      )}

      {/* Live Search Panel */}
      <div className="mt-8">
        <LiveSearchPanel />
      </div>
    </div>
  );
}

function IntelCard({ item }: { item: IntelItem }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = INTEL_CAT_CONFIG[item.category] || INTEL_CAT_CONFIG.news;

  return (
    <div className={`bg-card border rounded-2xl overflow-hidden transition-all shadow-sm ${
      item.priority === "TOP PRIORITY" ? "border-red-300" : item.is_actionable ? "border-teal/40" : "border-border"
    }`}>
      <div className="px-5 py-4 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${cfg.color} ${cfg.borderColor}`}>
                {cfg.icon} {cfg.label}
              </span>
              {item.priority && (
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${
                  PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.MEDIUM
                }`}>{item.priority}</span>
              )}
              {item.tender_type && (
                <span className="px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-bold">
                  {item.tender_type}
                </span>
              )}
              {item.is_actionable && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold">
                  ⚡ Actionable
                </span>
              )}
              {item.region && <span className="text-muted-foreground text-xs">{item.region}</span>}
            </div>
            <h4 className="text-sm font-bold text-foreground">{item.title}</h4>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.summary}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {item.estimated_value && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold">
                {item.estimated_value}
              </span>
            )}
            <span className="text-muted-foreground text-sm">{expanded ? "▲" : "▼"}</span>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 space-y-3 border-t border-border pt-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            {item.operator && <div><span className="text-muted-foreground">Operator:</span> <span className="text-foreground ml-1 font-semibold">{item.operator}</span></div>}
            {item.vessel_type && <div><span className="text-muted-foreground">Vessel Type:</span> <span className="text-foreground ml-1">{item.vessel_type}</span></div>}
            {item.deadline && <div><span className="text-muted-foreground">Deadline:</span> <span className="text-red-600 ml-1 font-semibold">{item.deadline}</span></div>}
            {item.source && <div><span className="text-muted-foreground">Source:</span> <span className="text-foreground ml-1">{item.source}</span></div>}
          </div>

          {item.vessel_recommendation && (
            <div className="bg-teal/5 border border-teal/20 rounded-xl p-3">
              <div className="text-[10px] font-mono text-teal uppercase mb-1">🚢 Vessel Recommendation</div>
              <p className="text-sm text-foreground">{item.vessel_recommendation}</p>
            </div>
          )}

          {item.velocity_fit && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="text-[10px] font-mono text-blue-600 uppercase mb-1">⚡ Velocity Offshore Fit</div>
              <p className="text-sm text-foreground">{item.velocity_fit}</p>
            </div>
          )}

          {item.ai_analysis && (
            <div className="bg-muted/50 border border-border rounded-xl p-3">
              <div className="text-[10px] font-mono text-muted-foreground uppercase mb-1">🤖 AI Analysis</div>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.ai_analysis}</p>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-muted-foreground">
              Generated: {new Date(item.fetched_at).toLocaleDateString()}
            </span>
            {item.source_url && (
              <a href={item.source_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-teal hover:underline font-semibold">
                View Source →
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VESSEL MODAL ───
function VesselModal({ vessel, allFiltered, onClose, onNavigate, onFilterOperator }: {
  vessel: Vessel;
  allFiltered: Vessel[];
  onClose: () => void;
  onNavigate: (v: Vessel) => void;
  onFilterOperator: (op: string) => void;
}) {
  const idx = allFiltered.findIndex(v => v.id === vessel.id);
  const hasPrev = idx > 0;
  const hasNext = idx < allFiltered.length - 1;
  const bdCtx = BD_CONTEXT[vessel.id];

  const Row = ({ label, value, highlight }: { label: string; value: string | number | null | undefined; highlight?: boolean }) => value ? (
    <div className="flex gap-3 py-2 border-b border-border">
      <span className="text-[11px] text-muted-foreground uppercase tracking-wider w-32 flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm ${highlight ? "text-teal font-semibold" : "text-foreground"}`}>{value}</span>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 bg-foreground/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-card border-b border-border px-6 py-5 z-10 rounded-t-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <TypeBadge type={vessel.type} />
                <span className={`text-xs font-semibold ${STATUS_COLORS[vessel.status] || "text-muted-foreground"}`}>{vessel.status}</span>
                <RelBadge rel={vessel.relevance} />
              </div>
              <h2 className="text-xl font-black text-foreground leading-tight font-display">{vessel.name}</h2>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <RegionBadge region={vessel.region} />
                <span className="text-muted-foreground text-xs">{vessel.country}</span>
                {vessel.basin && <span className="text-muted-foreground/60 text-[10px]">({vessel.basin})</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button disabled={!hasPrev} onClick={() => onNavigate(allFiltered[idx - 1])}
                className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 disabled:opacity-20 disabled:cursor-not-allowed transition-colors text-sm">←</button>
              <span className="text-[10px] text-muted-foreground/60 tabular-nums px-1">{idx+1}/{allFiltered.length}</span>
              <button disabled={!hasNext} onClick={() => onNavigate(allFiltered[idx + 1])}
                className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 disabled:opacity-20 disabled:cursor-not-allowed transition-colors text-sm">→</button>
              <button onClick={onClose} className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/25 transition-colors text-lg leading-none ml-1">×</button>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {bdCtx && (
            <div className={`rounded-xl border px-4 py-3.5 ${vessel.relevance === "HIGH" ? "bg-red-50 border-red-200" : "bg-yellow-50 border-yellow-200"}`}>
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase mb-1.5 text-red-600">
                {vessel.relevance === "HIGH" ? "⚑ Velocity BD Intelligence" : "BD Context"}
              </div>
              <p className="text-sm text-foreground leading-relaxed">{bdCtx}</p>
            </div>
          )}

          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] text-muted-foreground uppercase mb-3">Asset Details</div>
            <Row label="Operator" value={vessel.operator} />
            <Row label="Contractor" value={vessel.contractor} />
            <Row label="Field / Project" value={vessel.field} />
            <Row label="Country" value={vessel.country} />
            {vessel.basin && <Row label="Basin" value={vessel.basin} />}
            {vessel.status === "Active"
              ? <Row label="First Oil" value={vessel.firstOil} highlight />
              : <Row label="Delivery" value={vessel.delivery || "TBD"} highlight />
            }
            {vessel.generation && <Row label="Generation" value={vessel.generation} />}
            {vessel.currentContract && <Row label="Current Contract" value={vessel.currentContract} />}
            {vessel.dpClass && <Row label="DP Class" value={vessel.dpClass} />}
            {vessel.flag && <Row label="Flag" value={vessel.flag} />}
            {vessel.hullMaterial && <Row label="Hull" value={vessel.hullMaterial} />}
          </div>

          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] text-muted-foreground uppercase mb-3">Technical Specifications</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {vessel.capacity && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.capacity.toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">bpd capacity</div>
                </div>
              )}
              {vessel.storage && vessel.storage !== "N/A" && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-sm font-black text-foreground">{vessel.storage}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">storage (bbl) / DWT</div>
                </div>
              )}
              {vessel.waterDepth && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.waterDepth.toLocaleString()}m</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">water depth</div>
                </div>
              )}
              {vessel.drillDepth && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.drillDepth.toLocaleString()}m</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">drill depth</div>
                </div>
              )}
              {vessel.vdl && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.vdl.toLocaleString()}t</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">VDL</div>
                </div>
              )}
              {vessel.loa && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.loa}m</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">LOA</div>
                </div>
              )}
              {vessel.dwt && vessel.dwt > 0 && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.dwt.toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">DWT</div>
                </div>
              )}
              {vessel.paxCapacity && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.paxCapacity}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">PAX capacity</div>
                </div>
              )}
              {vessel.speed && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.speed}kn</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">max speed</div>
                </div>
              )}
              {vessel.bhp && vessel.bhp > 0 && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.bhp.toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">BHP</div>
                </div>
              )}
              {vessel.bollardPull && vessel.bollardPull > 0 && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.bollardPull}t</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">bollard pull</div>
                </div>
              )}
              {vessel.craneCap && (
                <div className="bg-muted/50 border border-border rounded-xl p-3 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{vessel.craneCap.toLocaleString()}t</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">crane capacity</div>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] text-muted-foreground uppercase mb-2">Intelligence Notes</div>
            <p className="text-sm text-muted-foreground leading-relaxed bg-muted/50 border border-border rounded-xl px-4 py-3">{vessel.notes || "No notes recorded."}</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={() => { onFilterOperator(vessel.operator); onClose(); }}
              className="flex-1 px-4 py-2.5 bg-teal/10 hover:bg-teal/20 border border-teal/30 hover:border-teal/50 text-teal rounded-xl text-xs font-bold tracking-wide transition-all text-center">
              View All {vessel.operator} Assets →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── UPDATE MODAL ───
function UpdateModal({ onClose }: { onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(UPDATE_PROTOCOL.prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="fixed inset-0 bg-foreground/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="text-xs font-mono tracking-widest text-teal uppercase mb-1">Data Refresh Protocol</div>
            <h2 className="text-xl font-black text-foreground font-display">Update Fleet Intelligence</h2>
            <p className="text-muted-foreground text-xs mt-1">Last updated: {UPDATE_PROTOCOL.lastUpdated}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none mt-1">×</button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">Methodology</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{UPDATE_PROTOCOL.methodology}</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Research Prompt</h3>
              <button onClick={copy}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${copied ? "bg-teal border-teal text-white" : "bg-muted border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"}`}>
                {copied ? "✓ Copied!" : "Copy Prompt"}
              </button>
            </div>
            <pre className="bg-muted border border-border rounded-xl p-4 text-xs text-muted-foreground font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed">
              {UPDATE_PROTOCOL.prompt}
            </pre>
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">Primary Sources</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {UPDATE_PROTOCOL.sources.map(s => (
                <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-muted/50 hover:bg-muted border border-border hover:border-foreground/15 rounded-lg px-3 py-2 transition-colors group">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0"></span>
                  <span className="text-xs text-muted-foreground group-hover:text-foreground truncate">{s.name}</span>
                  <span className="text-muted-foreground/40 text-xs ml-auto group-hover:text-teal">↗</span>
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-border flex justify-end">
          <button onClick={onClose} className="px-6 py-2 bg-muted hover:bg-muted/80 border border-border text-foreground rounded-lg text-sm font-semibold transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── MARKET SUMMARY TAB ───
function MarketSummaryPanel() {
  const totalVessels = MARKET_SUMMARY.reduce((s, m) => s + m.totalVessels, 0);
  const totalActive = MARKET_SUMMARY.reduce((s, m) => s + m.active, 0);
  const totalUC = MARKET_SUMMARY.reduce((s, m) => s + m.underConstruction, 0);

  const regionBreakdown = useMemo(() => {
    const counts: Record<string, { total: number; high: number }> = {};
    FLEET.forEach(v => {
      if (!counts[v.region]) counts[v.region] = { total: 0, high: 0 };
      counts[v.region].total++;
      if (v.relevance === "HIGH") counts[v.region].high++;
    });
    return Object.entries(counts).sort((a, b) => b[1].total - a[1].total);
  }, []);

  return (
    <div className="space-y-6">
      {/* Hero stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { v: FLEET.length, l: "Assets in Register", sub: `${totalVessels} market total`, accent: "text-foreground" },
          { v: FLEET.filter(v => v.status === "Active").length, l: "Active Assets Tracked", sub: `${totalActive} market active`, accent: "text-emerald-600" },
          { v: FLEET.filter(v => v.status === "Under Construction" || v.status === "Planned").length, l: "Pipeline / Under Construction", sub: `${totalUC} market newbuilds`, accent: "text-yellow-600" },
          { v: FLEET.filter(v => v.relevance === "HIGH").length, l: "High BD Priority", sub: "Velocity target assets", accent: "text-red-600" },
        ].map(s => (
          <div key={s.l} className="bg-card border border-border rounded-2xl px-5 py-5 text-center shadow-sm">
            <div className={`text-4xl font-black ${s.accent} tabular-nums`}>{s.v}</div>
            <div className="text-xs text-foreground font-semibold mt-1">{s.l}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Market Overview Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-border">
          <div className="text-[10px] font-mono tracking-[0.25em] text-teal uppercase">Global Market Overview</div>
          <h3 className="text-lg font-black text-foreground mt-1 font-display">Offshore Vessel Market — March 2026</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="py-3 px-4 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Category</th>
                <th className="py-3 px-4 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total Market</th>
                <th className="py-3 px-4 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Active</th>
                <th className="py-3 px-4 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Newbuild / UC</th>
                <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">BD Relevance</th>
              </tr>
            </thead>
            <tbody>
              {MARKET_SUMMARY.map((m, i) => (
                <tr key={m.category} className={`border-b border-border ${i%2 === 0 ? "" : "bg-muted/30"}`}>
                  <td className="py-3 px-4 text-sm font-semibold text-foreground">{m.category}</td>
                  <td className="py-3 px-4 text-right text-sm text-foreground tabular-nums font-bold">{m.totalVessels}</td>
                  <td className="py-3 px-4 text-right text-sm text-emerald-600 tabular-nums">{m.active}</td>
                  <td className="py-3 px-4 text-right text-sm text-yellow-600 tabular-nums">{m.underConstruction}</td>
                  <td className="py-3 px-4 text-center"><RelBadge rel={m.bdRelevance} /></td>
                </tr>
              ))}
              <tr className="border-t-2 border-foreground/15 bg-muted/50">
                <td className="py-3 px-4 text-sm font-black text-foreground uppercase tracking-wider">Total</td>
                <td className="py-3 px-4 text-right text-sm text-foreground font-black tabular-nums">{totalVessels}</td>
                <td className="py-3 px-4 text-right text-sm text-emerald-600 font-black tabular-nums">{totalActive}</td>
                <td className="py-3 px-4 text-right text-sm text-yellow-600 font-black tabular-nums">{totalUC}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Region breakdown */}
      <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
        <div className="text-[10px] font-mono tracking-[0.25em] text-teal uppercase mb-4">Intelligence Register — Regional Breakdown</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {regionBreakdown.map(([region, data]) => (
            <div key={region} className="bg-muted/50 border border-border rounded-xl p-3">
              <RegionBadge region={region} />
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-black text-foreground tabular-nums">{data.total}</span>
                <span className="text-[10px] text-muted-foreground">assets</span>
              </div>
              {data.high > 0 && (
                <div className="text-[10px] text-red-600 mt-1 font-semibold">{data.high} high BD priority</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── BD INTELLIGENCE TAB ───
function BDIntelligencePanel() {
  return (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
        <div className="text-[10px] font-mono tracking-[0.25em] text-red-600 uppercase mb-2">⚑ Velocity BD Pipeline</div>
        <h3 className="text-lg font-black text-foreground font-display">Business Development Opportunities</h3>
        <p className="text-muted-foreground text-sm mt-1">Strategic targets for AIRCAT 35 crew transfer and supply vessel deployment</p>
      </div>

      <div className="space-y-4">
        {BD_OPPORTUNITIES.map((bd) => (
          <BDCard key={bd.id} bd={bd} />
        ))}
      </div>
    </div>
  );
}

function BDCard({ bd }: { bd: BDOpportunity }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`bg-card border rounded-2xl overflow-hidden transition-all shadow-sm ${
      bd.priority === "TOP PRIORITY" ? "border-red-300" : "border-border"
    }`}>
      <div className="px-5 py-4 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${PRIORITY_COLORS[bd.priority] || PRIORITY_COLORS.MEDIUM}`}>
                {bd.priority}
              </span>
              <span className="text-muted-foreground text-xs">{bd.market}</span>
            </div>
            <h4 className="text-sm font-bold text-foreground">{bd.operator} — {bd.asset}</h4>
            <p className="text-xs text-muted-foreground mt-1">{bd.vesselNeed}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${
              bd.opportunitySize.startsWith("HIGH") ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
              bd.opportunitySize.startsWith("MEDIUM") ? "bg-yellow-50 border-yellow-200 text-yellow-700" :
              "bg-slate-100 border-slate-200 text-slate-500"
            }`}>{bd.opportunitySize.split("—")[0].trim()}</span>
            <span className="text-muted-foreground text-sm">{expanded ? "▲" : "▼"}</span>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 space-y-3 border-t border-border pt-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><span className="text-muted-foreground">Current Provider:</span> <span className="text-foreground ml-1">{bd.currentProvider}</span></div>
            <div><span className="text-muted-foreground">Contract Status:</span> <span className="text-foreground ml-1">{bd.contractStatus}</span></div>
            <div><span className="text-muted-foreground">Velocity Fit:</span> <span className="text-teal ml-1 font-semibold">{bd.velocityFit}</span></div>
            <div><span className="text-muted-foreground">Opportunity:</span> <span className="text-foreground ml-1">{bd.opportunitySize}</span></div>
          </div>
          <div className="bg-teal/5 border border-teal/20 rounded-xl p-3">
            <div className="text-[10px] font-mono text-teal uppercase mb-1">Next Action</div>
            <p className="text-sm text-foreground">{bd.nextAction}</p>
          </div>
          {bd.notes && (
            <p className="text-xs text-muted-foreground leading-relaxed bg-muted/50 border border-border rounded-lg px-3 py-2">{bd.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───
const EmployeeFleetIntelligence = () => {
  const [activeTab, setActiveTab] = useState<MainTab>("summary");
  const [regionFilter, setRegionFilter] = useState("All");
  const [relFilter, setRelFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [operatorFilter, setOperatorFilter] = useState("");
  const [operatorSearch, setOperatorSearch] = useState("");
  const [showOperatorDropdown, setShowOperatorDropdown] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
  const [sortKey, setSortKey] = useState<keyof Vessel>("type");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const isVesselTab = activeTab !== "bd" && activeTab !== "summary" && activeTab !== "intel";
  const categoryConfig = isVesselTab ? CATEGORY_CONFIG[activeTab as VesselCategory] : null;

  const [typeFilter, setTypeFilter] = useState("All");

  const categoryVessels = useMemo(() => {
    if (!isVesselTab) return [];
    return FLEET.filter(v => v.category === activeTab);
  }, [activeTab, isVesselTab]);

  const allOperators = useMemo(() => {
    const counts: Record<string, number> = {};
    categoryVessels.forEach(v => { counts[v.operator] = (counts[v.operator] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([op]) => op);
  }, [categoryVessels]);

  const filteredOperatorOptions = useMemo(() =>
    allOperators.filter(op => op.toLowerCase().includes(operatorSearch.toLowerCase())),
    [allOperators, operatorSearch]
  );

  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    categoryVessels.forEach(v => { c[v.type] = (c[v.type] || 0) + 1; });
    return c;
  }, [categoryVessels]);

  const filtered = useMemo(() => {
    return categoryVessels.filter(v => {
      if (typeFilter !== "All" && v.type !== typeFilter) return false;
      if (regionFilter !== "All" && v.region !== regionFilter) return false;
      if (relFilter !== "All" && v.relevance !== relFilter) return false;
      if (statusFilter !== "All" && v.status !== statusFilter) return false;
      if (operatorFilter && v.operator !== operatorFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![v.name, v.operator, v.field, v.country, v.contractor, v.notes].some(f => f?.toLowerCase().includes(q))) return false;
      }
      return true;
    }).sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [categoryVessels, typeFilter, regionFilter, relFilter, statusFilter, operatorFilter, search, sortKey, sortDir]);

  const toggleSort = (k: keyof Vessel) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const SortTh = ({ label, k, align = "left" }: { label: string; k: keyof Vessel; align?: string }) => (
    <th
      className={`py-2.5 px-3 text-${align} text-[10px] font-bold uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground whitespace-nowrap select-none`}
      onClick={() => toggleSort(k)}
    >
      {label}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );

  const clearFilters = () => {
    setSearch(""); setTypeFilter("All"); setRegionFilter("All"); setRelFilter("All"); setStatusFilter("All"); setOperatorFilter(""); setOperatorSearch("");
  };

  const handleTabChange = (tab: MainTab) => {
    setActiveTab(tab);
    clearFilters();
  };

  const totalActive = FLEET.filter(v => v.status === "Active").length;
  const totalUC = FLEET.filter(v => v.status === "Under Construction" || v.status === "Planned").length;

  return (
    <IntranetLayout>
      <div className="text-foreground" onClick={() => setShowOperatorDropdown(false)}>

        {showUpdateModal && <UpdateModal onClose={() => setShowUpdateModal(false)} />}
        {selectedVessel && (
          <VesselModal
            vessel={selectedVessel}
            allFiltered={filtered}
            onClose={() => setSelectedVessel(null)}
            onNavigate={setSelectedVessel}
            onFilterOperator={(op) => { setOperatorFilter(op); setOperatorSearch(""); }}
          />
        )}

        {/* HEADER */}
        <div className="bg-card border border-border rounded-2xl px-6 py-6 mb-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-5">
              <img src={velocityLogoFull} alt="Velocity Offshore" className="h-14 object-contain hidden sm:block" />
              <div>
                <div className="text-[10px] font-mono tracking-[0.3em] text-teal uppercase mb-1">Fleet Intelligence Register</div>
                <h1 className="text-2xl lg:text-3xl font-black tracking-tight leading-none font-display text-foreground">
                  Fleet Intelligence
                </h1>
                <p className="text-muted-foreground text-xs mt-1.5">{FLEET.length} assets tracked · {totalActive} active · {totalUC} pipeline · March 2026</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {[
                { v: FLEET.length, l: "Total Assets" },
                { v: totalActive, l: "Active" },
                { v: totalUC, l: "Pipeline" },
                { v: FLEET.filter(v => v.relevance === "HIGH").length, l: "High BD" },
              ].map(s => (
                <div key={s.l} className="bg-muted/50 border border-border rounded-xl px-4 py-3 text-center min-w-[72px]">
                  <div className="text-2xl font-black text-foreground">{s.v}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{s.l}</div>
                </div>
              ))}
              <button onClick={() => setShowUpdateModal(true)}
                className="flex items-center gap-2 px-4 py-3 bg-teal/10 hover:bg-teal/20 border border-teal/30 hover:border-teal/50 text-teal rounded-xl text-xs font-bold tracking-wide transition-all">
                ↻ Update Data
              </button>
            </div>
          </div>

          {/* CATEGORY TABS */}
          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-border">
            {TAB_ORDER.map(t => {
              const count = t.key === "summary" ? FLEET.length : t.key === "bd" ? BD_OPPORTUNITIES.length : t.key === "intel" ? null : FLEET.filter(v => v.category === t.key).length;
              const active = activeTab === t.key;
              return (
                <button key={t.key} onClick={() => handleTabChange(t.key)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-bold transition-all ${
                    active
                      ? "bg-teal/10 border-teal/40 text-teal shadow-sm"
                      : "bg-muted/30 border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
                  }`}>
                  <span>{t.icon}</span>
                  {t.label}
                  {count !== null && <span className={`text-[10px] ${active ? "opacity-80" : "opacity-50"}`}>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* CONTENT */}
        <div>
          {activeTab === "summary" && <MarketSummaryPanel />}
          {activeTab === "bd" && <BDIntelligencePanel />}

          {isVesselTab && (
            <>
              {/* Sub-type tabs */}
              {categoryConfig && categoryConfig.types.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <button onClick={() => setTypeFilter("All")}
                    className={`px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${
                      typeFilter === "All" ? "bg-foreground text-background border-foreground" : "bg-muted/30 border-border text-muted-foreground hover:text-foreground"
                    }`}>All <span className="opacity-60">{categoryVessels.length}</span></button>
                  {categoryConfig.types.map(t => {
                    const c = typeCounts[t] || 0;
                    if (c === 0) return null;
                    const tc = TYPE_COLORS[t];
                    return (
                      <button key={t} onClick={() => setTypeFilter(t)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${
                          typeFilter === t ? (tc?.pill || "bg-foreground text-background border-foreground") : "bg-muted/30 border-border text-muted-foreground hover:text-foreground"
                        }`}>
                        {tc && <span className={`w-1.5 h-1.5 rounded-full ${tc.dot}`}></span>}
                        {t} <span className="opacity-60">{c}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* FILTERS */}
              <div className="flex flex-wrap gap-3 items-center mb-4">
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search name, operator, field, notes…"
                  className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal/50 w-64" />

                {/* Operator filter */}
                <div className="relative" onClick={e => e.stopPropagation()}>
                  <div className={`flex items-center gap-2 bg-card border rounded-lg px-3 py-2 cursor-pointer transition-colors w-52 ${operatorFilter ? "border-teal bg-teal/5" : "border-border hover:border-foreground/25"}`}
                    onClick={() => setShowOperatorDropdown(v => !v)}>
                    <span className={`text-xs font-semibold truncate flex-1 ${operatorFilter ? "text-teal" : "text-muted-foreground"}`}>
                      {operatorFilter || "Filter by Operator"}
                    </span>
                    {operatorFilter
                      ? <button onClick={e => { e.stopPropagation(); setOperatorFilter(""); setOperatorSearch(""); }} className="text-muted-foreground hover:text-foreground text-xs">✕</button>
                      : <span className="text-muted-foreground/50 text-xs">▾</span>
                    }
                  </div>
                  {showOperatorDropdown && (
                    <div className="absolute top-full left-0 mt-1 z-30 bg-card border border-border rounded-xl shadow-xl w-64 overflow-hidden">
                      <div className="p-2 border-b border-border">
                        <input autoFocus value={operatorSearch} onChange={e => setOperatorSearch(e.target.value)}
                          placeholder="Search operators…"
                          className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-teal/30" />
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {filteredOperatorOptions.map(op => {
                          const count = categoryVessels.filter(v => v.operator === op).length;
                          const hasHigh = categoryVessels.some(v => v.operator === op && v.relevance === "HIGH");
                          return (
                            <button key={op} onClick={() => { setOperatorFilter(op); setOperatorSearch(""); setShowOperatorDropdown(false); }}
                              className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-muted transition-colors text-left ${operatorFilter === op ? "bg-teal/10 text-teal" : "text-foreground"}`}>
                              <span className="flex items-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${hasHigh ? "bg-red-500" : "bg-muted-foreground/30"}`}></span>
                                {op}
                              </span>
                              <span className="text-muted-foreground font-mono">{count}</span>
                            </button>
                          );
                        })}
                        {filteredOperatorOptions.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground text-center">No operators found</div>}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Region:</span>
                  {ALL_REGIONS.map(r => (
                    <button key={r} onClick={() => setRegionFilter(r)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                        regionFilter === r ? "bg-teal text-white border-teal" : "bg-muted/30 text-muted-foreground border-border hover:border-foreground/20 hover:text-foreground"
                      }`}>{r}</button>
                  ))}
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">BD:</span>
                  {["All","HIGH","MEDIUM","LOW"].map(r => (
                    <button key={r} onClick={() => setRelFilter(r)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                        relFilter === r ? REL_COLORS[r] || "bg-foreground text-background border-foreground" : "bg-muted/30 text-muted-foreground border-border hover:border-foreground/20"
                      }`}>{r}</button>
                  ))}
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Status:</span>
                  {["All","Active","Under Construction","Planned"].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                        statusFilter === s ? "bg-foreground text-background border-foreground" : "bg-muted/30 text-muted-foreground border-border hover:border-foreground/20"
                      }`}>{s}</button>
                  ))}
                </div>

                {(search || typeFilter !== "All" || regionFilter !== "All" || relFilter !== "All" || statusFilter !== "All" || operatorFilter) && (
                  <button onClick={clearFilters}
                    className="text-[11px] text-muted-foreground hover:text-foreground border border-border hover:border-foreground/25 px-2.5 py-1 rounded-full transition-colors">
                    ✕ Clear
                  </button>
                )}

                <span className="ml-auto text-[11px] text-muted-foreground">{filtered.length} assets shown</span>
              </div>

              {/* CLIENT FLEET SUMMARY */}
              {operatorFilter && (() => {
                const fleet = categoryVessels.filter(v => v.operator === operatorFilter);
                const byType: Record<string, number> = {};
                fleet.forEach(v => { byType[v.type] = (byType[v.type] || 0) + 1; });
                const regions = [...new Set(fleet.map(v => v.region))];
                return (
                  <div className="mb-4 bg-teal/5 border border-teal/20 rounded-2xl p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="text-[10px] font-mono tracking-[0.25em] text-teal uppercase mb-1">Client Fleet View</div>
                        <h2 className="text-xl font-black text-foreground font-display">{operatorFilter}</h2>
                        <div className="flex flex-wrap gap-1.5 mt-2">{regions.map(r => <RegionBadge key={r} region={r} />)}</div>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { v: fleet.length, l: "Total", col: "text-foreground" },
                          { v: fleet.filter(v => v.status === "Active").length, l: "Active", col: "text-emerald-600" },
                          { v: fleet.filter(v => v.relevance === "HIGH").length, l: "High BD", col: "text-red-600" },
                        ].filter(s => s.v > 0).map(s => (
                          <div key={s.l} className="bg-card border border-border rounded-xl px-4 py-2.5 text-center min-w-[60px]">
                            <div className={`text-xl font-black ${s.col}`}>{s.v}</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">{s.l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-teal/15">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider self-center">Mix:</span>
                      {Object.entries(byType).map(([type, count]) => (
                        <span key={type} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-bold ${TYPE_COLORS[type]?.pill || "bg-muted border-border text-muted-foreground"}`}>
                          {type} × {count}
                        </span>
                      ))}
                      <button onClick={() => { setOperatorFilter(""); setOperatorSearch(""); }}
                        className="ml-auto text-[11px] text-muted-foreground hover:text-foreground border border-border px-3 py-1 rounded-full transition-colors">✕ Clear</button>
                    </div>
                  </div>
                );
              })()}

              {/* TABLE */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ minWidth: 1100 }}>
                    <thead className="border-b border-border bg-muted/50">
                      <tr>
                        <SortTh label="Type" k="type" />
                        <SortTh label="Asset Name" k="name" />
                        <SortTh label="Operator" k="operator" />
                        <SortTh label="Contractor" k="contractor" />
                        <SortTh label="Field" k="field" />
                        <SortTh label="Region" k="region" />
                        <SortTh label="Status" k="status" />
                        <th className="py-2.5 px-3 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">First Oil / Delivery</th>
                        {(activeTab === "production") && (
                          <>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Cap (bpd)</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Storage</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Depth (m)</th>
                          </>
                        )}
                        {(activeTab === "drilling") && (
                          <>
                            <th className="py-2.5 px-3 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Gen</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Drill Depth</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">VDL (t)</th>
                          </>
                        )}
                        {(activeTab === "osv") && (
                          <>
                            <th className="py-2.5 px-3 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">DP</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">DWT</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">LOA (m)</th>
                          </>
                        )}
                        {(activeTab === "ctv") && (
                          <>
                            <th className="py-2.5 px-3 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">DP</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">PAX</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Speed (kn)</th>
                          </>
                        )}
                        {(activeTab === "specialised") && (
                          <>
                            <th className="py-2.5 px-3 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">DP</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">LOA (m)</th>
                            <th className="py-2.5 px-3 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Crane (t)</th>
                          </>
                        )}
                        <th className="py-2.5 px-3 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground max-w-[200px]">Notes</th>
                        <SortTh label="BD" k="relevance" align="center" />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((v, i) => (
                        <tr key={`${v.id}-${v.category}`} className={`border-b border-border hover:bg-muted/50 transition-colors cursor-pointer ${i%2===0 ? "" : "bg-muted/20"}`}
                          onClick={() => setSelectedVessel(v)}>
                          <td className="py-2 px-3"><TypeBadge type={v.type} /></td>
                          <td className="py-2 px-3">
                            <span className={`font-semibold text-sm ${v.relevance === "HIGH" ? "text-foreground" : "text-foreground/80"}`}>{v.name}</span>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground text-xs" onClick={e => e.stopPropagation()}>
                            <button onClick={() => { setOperatorFilter(v.operator); setOperatorSearch(""); setShowOperatorDropdown(false); }}
                              className="hover:text-teal hover:underline transition-colors text-left">
                              {v.operator}
                            </button>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground/70 text-xs">{v.contractor}</td>
                          <td className="py-2 px-3 text-muted-foreground/70 text-xs italic">{v.field}</td>
                          <td className="py-2 px-3"><RegionBadge region={v.region} /></td>
                          <td className="py-2 px-3">
                            <span className={`text-xs font-semibold ${STATUS_COLORS[v.status] || "text-muted-foreground"}`}>{v.status}</span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            {v.status === "Active"
                              ? <span className="text-emerald-600 text-xs font-semibold">{v.firstOil || "—"}</span>
                              : <span className={`text-xs font-bold ${DELIVERY_COLORS(v.delivery)}`}>{v.delivery || "TBD"}</span>
                            }
                          </td>
                          {activeTab === "production" && (
                            <>
                              <td className="py-2 px-3 text-right text-muted-foreground text-xs tabular-nums">{v.capacity ? v.capacity.toLocaleString() : "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground/70 text-xs">{v.storage || "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground/60 text-xs tabular-nums">{v.waterDepth ? v.waterDepth.toLocaleString() : "—"}</td>
                            </>
                          )}
                          {activeTab === "drilling" && (
                            <>
                              <td className="py-2 px-3 text-center text-muted-foreground text-xs">{v.generation || "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground text-xs tabular-nums">{v.drillDepth ? v.drillDepth.toLocaleString() + "m" : "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground/70 text-xs tabular-nums">{v.vdl ? v.vdl.toLocaleString() : "—"}</td>
                            </>
                          )}
                          {activeTab === "osv" && (
                            <>
                              <td className="py-2 px-3 text-center text-muted-foreground text-xs">{v.dpClass || "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground text-xs tabular-nums">{v.dwt && v.dwt > 0 ? v.dwt.toLocaleString() : (v.storage !== "N/A" ? v.storage : "—")}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground/70 text-xs tabular-nums">{v.loa || "—"}</td>
                            </>
                          )}
                          {activeTab === "ctv" && (
                            <>
                              <td className="py-2 px-3 text-center text-muted-foreground text-xs">{v.dpClass || "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground text-xs tabular-nums">{v.paxCapacity || "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground/70 text-xs tabular-nums">{v.speed ? v.speed + "kn" : "—"}</td>
                            </>
                          )}
                          {activeTab === "specialised" && (
                            <>
                              <td className="py-2 px-3 text-center text-muted-foreground text-xs">{v.dpClass || "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground text-xs tabular-nums">{v.loa || "—"}</td>
                              <td className="py-2 px-3 text-right text-muted-foreground/70 text-xs tabular-nums">{v.craneCap ? v.craneCap.toLocaleString() : "—"}</td>
                            </>
                          )}
                          <td className="py-2 px-3 text-muted-foreground/60 text-xs max-w-[200px] truncate" title={v.notes}>{v.notes}</td>
                          <td className="py-2 px-3 text-center"><RelBadge rel={v.relevance} /></td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr><td colSpan={12} className="py-16 text-center text-muted-foreground text-sm">No assets match your filters.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-[11px] text-muted-foreground border-t border-border pt-4">
                <span><span className="text-red-600 font-bold">HIGH BD</span> = ExxonMobil Worldwide / Azule Energy / direct Velocity market</span>
                <span><span className="text-emerald-600 font-bold">2026</span> deliveries = near-term crew transfer demand</span>
                <span>Click any <span className="text-teal font-semibold">vessel</span> to drill down · Click <span className="text-teal font-semibold">operator</span> to view fleet</span>
                <button onClick={() => setShowUpdateModal(true)} className="ml-auto text-teal hover:text-teal-light font-semibold transition-colors">
                  ↻ Update Protocol
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </IntranetLayout>
  );
};

export default EmployeeFleetIntelligence;
